import { useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { formatEuro, type PaymentInfo, type PublicOrderView } from '@avh/shared';
import { Container } from '@/components/layout/PublicLayout';
import { ApiStateView } from '@/components/ui/ApiStateView';
import { Button, LinkButton } from '@/components/ui/Button';
import { Alert, Card, StatusBadge } from '@/components/ui/Feedback';
import { useI18n } from '@/contexts/I18n';
import { formatDate, formatDateTime, groupsOfFour, localized } from '@/lib/format';
import { useApi } from '@/lib/useApi';

function CopyValue({ value, display }: { value: string; display: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Zwischenablage nicht verfügbar – Wert bleibt sichtbar zum Abschreiben.
    }
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="font-mono font-semibold tracking-wide">{display}</span>
      <button type="button" onClick={copy} className="rounded px-1.5 py-0.5 text-xs font-semibold text-brand-600 hover:bg-brand-50">
        {copied ? t.order.copied : t.order.copy}
      </button>
    </span>
  );
}

function PaymentBox({ payment, dueAt }: { payment: PaymentInfo; dueAt: string | null }) {
  const { t, locale } = useI18n();
  return (
    <Card className="border-l-4 border-accent-500">
      <h2 className="text-xl font-bold text-brand-900">
        {t.order.payTitle} {formatEuro(payment.amountCents, locale)}
      </h2>
      <p className="mt-1 text-sm text-muted">{t.order.payIntro(dueAt ? formatDate(dueAt, locale) : '–')}</p>
      <div className="mt-5 grid gap-6 sm:grid-cols-[1fr_auto]">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted">{t.order.recipient}</dt>
          <dd className="font-semibold">{payment.recipient}</dd>
          <dt className="text-muted">IBAN</dt>
          <dd>
            <CopyValue value={payment.iban} display={groupsOfFour(payment.iban)} />
          </dd>
          {payment.bic ? (
            <>
              <dt className="text-muted">BIC</dt>
              <dd className="font-mono font-semibold">{payment.bic}</dd>
            </>
          ) : null}
          <dt className="text-muted">{t.order.amount}</dt>
          <dd className="font-semibold">{formatEuro(payment.amountCents, locale)}</dd>
          <dt className="text-muted">{t.order.reference}</dt>
          <dd>
            <CopyValue value={payment.reference} display={groupsOfFour(payment.reference)} />
          </dd>
        </dl>
        <figure className="flex flex-col items-center gap-2">
          <img src={payment.epcQrDataUrl} alt={t.order.scanHint} className="size-44 rounded bg-white" />
          <figcaption className="max-w-44 text-center text-xs text-muted">{t.order.scanHint}</figcaption>
        </figure>
      </div>
      <p className="mt-4 text-sm font-semibold text-accent-700">{t.order.referenceHint}</p>
    </Card>
  );
}

function OrderDetails({ order, justOrdered }: { order: PublicOrderView; justOrdered: boolean }) {
  const { t, locale } = useI18n();
  const isFree = order.totalCents === 0;
  const key = encodeURIComponent(order.accessKey);
  const invoiceUrl = `/api/public/orders/${order.id}/invoice.pdf?k=${key}`;
  const ticketsUrl = `/api/public/orders/${order.id}/tickets.pdf?k=${key}`;
  const statusLabel = isFree && order.status === 'paid' ? t.order.statusFree : t.order.status[order.status];

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      {justOrdered ? (
        <Alert tone="success" title={isFree ? t.order.thanksFree : t.order.thanks}>
          {t.order.mailSent(order.buyer.email)}
        </Alert>
      ) : null}

      <div>
        <p className="text-sm font-semibold tracking-wide text-brand-600 uppercase">
          {t.order.number} {order.id}
        </p>
        <h1 className="mt-1 text-3xl font-bold text-brand-900">{localized(locale, order.event.title, order.event.titleEn)}</h1>
        <p className="mt-1 text-muted">
          {formatDateTime(order.event.startsAt, locale)} · {order.event.venueName}
        </p>
        <div className="mt-3">
          <StatusBadge status={order.status}>{statusLabel}</StatusBadge>
        </div>
      </div>

      {order.status === 'reserved' && order.payment ? (
        <PaymentBox payment={order.payment} dueAt={order.dueAt} />
      ) : null}
      {order.status === 'paid' ? (
        <Card className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p>{t.order.paidInfo}</p>
          <LinkButton href={ticketsUrl} target="_blank" rel="noopener" size="lg" className="shrink-0">
            {t.order.downloadTickets}
          </LinkButton>
        </Card>
      ) : null}
      {order.status === 'cancelled' ? <Alert tone="warning">{t.order.cancelledInfo}</Alert> : null}
      {order.status === 'expired' ? <Alert tone="warning">{t.order.expiredInfo}</Alert> : null}

      <Card>
        <h2 className="mb-3 font-bold text-brand-900">{t.order.summary}</h2>
        <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 text-sm">
          <dt>
            {order.quantity} × {t.order.tickets}
            {isFree ? '' : ` à ${formatEuro(order.unitPriceCents, locale)}`}
          </dt>
          <dd>{isFree ? '' : formatEuro(order.unitPriceCents * order.quantity, locale)}</dd>
          {order.discountPerTicketCents > 0 ? (
            <>
              <dt className="text-brand-700">{order.discountCode}</dt>
              <dd className="text-brand-700">−{formatEuro(order.discountPerTicketCents * order.quantity, locale)}</dd>
            </>
          ) : null}
          {isFree ? null : (
            <>
              <dt className="border-t border-neutral-200 pt-2 font-bold">{t.event.total}</dt>
              <dd className="border-t border-neutral-200 pt-2 font-bold">{formatEuro(order.totalCents, locale)}</dd>
            </>
          )}
        </dl>
        <p className="mt-4 text-sm text-muted">
          {order.buyer.firstName} {order.buyer.lastName} · {order.buyer.email}
        </p>
        {order.invoiceNumber ? (
          <LinkButton href={invoiceUrl} target="_blank" rel="noopener" variant="secondary" size="sm" className="mt-4">
            {t.order.downloadInvoice}
          </LinkButton>
        ) : null}
      </Card>

      <p className="text-sm text-muted">{t.order.saveLink}</p>
    </div>
  );
}

export function OrderPage() {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { t } = useI18n();
  const accessKey = searchParams.get('k') ?? '';
  const [state, reload] = useApi<PublicOrderView>(
    `/public/orders/${encodeURIComponent(id)}?k=${encodeURIComponent(accessKey)}`,
  );
  const justOrdered = (location.state as { justOrdered?: boolean } | null)?.justOrdered === true;

  return (
    <Container>
      <ApiStateView state={state} onRetry={reload} loadingLabel={t.common.loading} retryLabel={t.common.retry}>
        {(order) => <OrderDetails order={order} justOrdered={justOrdered} />}
      </ApiStateView>
      {state.status === 'success' && state.data.status === 'reserved' ? (
        <div className="mx-auto mt-6 max-w-3xl">
          <Button variant="ghost" size="sm" onClick={reload}>
            ↻ {t.common.retry}
          </Button>
        </div>
      ) : null}
    </Container>
  );
}
